import chroma from 'chroma-js';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { keyBy } from 'lodash-es';
import { useAuthStore } from '~/auth/stores/auth.store';
import { useCommonStore } from '~/common/stores/common.store';

dayjs.extend(utc);
dayjs.extend(timezone);

export function useBiDataFormatter() {
  const auth_store = useAuthStore();
  const common_store = useCommonStore();

  // Get user's timezone, fallback to browser timezone
  function getUserTimezone() {
    return auth_store?.logged_in_user_details?.timezone || dayjs.tz.guess();
  }

  // Format number with thousand separator and optional precision
  function formatNumber(value, compact = false) {
    if (value === null || value === undefined || value === '')
      return value;

    const num = Number(value);
    if (Number.isNaN(num))
      return value;

    return num.toLocaleString('en-US', {
      maximumFractionDigits: 2,
      ...(compact ? { notation: 'compact' } : {}),
    });
  }

  // Format integer with thousand separator
  function formatInteger(value) {
    return formatNumber(value);
  }

  // Format float with 2 decimal places and thousand separator
  function formatFloat(value) {
    return formatNumber(value);
  }

  // Format date as DD MMM YYYY in user's timezone
  function formatDate(value) {
    if (!value)
      return value;

    try {
      const date = dayjs(value);
      if (!date.isValid())
        return value;
      return date.format('DD MMMM YYYY');
    }
    catch (error) {
      console.warn('Error formatting date:', error);
      return value;
    }
  }

  // Format timestamp as DD MMM YYYY HH:MM A in user's timezone
  function formatTimestamp(value, has_timezone = false) {
    if (!value)
      return value;

    try {
      let date = null;
      if (has_timezone) {
        date = dayjs(value.replace('Z', ''));
      }
      else {
        const user_tz = getUserTimezone();
        date = dayjs(value).tz(user_tz);
      }
      if (!date.isValid())
        return value;
      return date.format('DD MMMM YYYY hh:mm A');
    }
    catch (error) {
      console.warn('Error formatting timestamp:', error);
      return value;
    }
  }

  // Generate consistent pastel colors for tags based on string hash
  function getPastelTagColor(str) {
    let hash = 0;
    const len = Math.min(str.length, 10);
    for (let i = 0; i < len; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    const pastel = chroma.hsl(hue, 0.45, 0.85);

    return {
      background: pastel.hex(),
      text: pastel.darken(3),
    };
  }

  function getLighterColor(color) {
    const base = chroma(color);
    const targetLuminance = base.luminance() > 0.6 ? 0.4 : 0.9;

    const contrastColor = base.luminance(targetLuminance);
    return contrastColor.hex();
  }

  // Create text node from value for DOM manipulation
  function createTextNodeFromValue(value) {
    return document.createTextNode(String(value ?? ''));
  }

  // Format array as HTML tags with consistent colors
  function formatArrayAsTags(value, defaultColor = null) {
    const formatted_value = Array.isArray(value) ? value : (value || '').split(',').map(v => v.trim()).filter(v => v.length);
    if (formatted_value.length === 0) {
      return createTextNodeFromValue(value);
    }

    const container = document.createElement('div');
    container.classList.add(
      'flex',
      'flex-wrap',
      'gap-[2px]',
      'items-center',
    );

    formatted_value.forEach((item) => {
      const tag = document.createElement('span');
      let color = '#fff';
      let textColor = '#000';
      if (typeof item === 'object') {
        tag.textContent = String(item.label);
        textColor = item.color;
        color = getLighterColor(textColor);
      }
      else {
        tag.textContent = item;
        const { background, text } = getPastelTagColor(String(item));
        color = background;
        textColor = text;
      }

      if (defaultColor) {
        color = defaultColor;
        textColor = '#222222';
      }

      tag.style.backgroundColor = color;
      tag.style.color = textColor;
      tag.classList.add(
        'inline-block',
        'px-2',
        'py-[2px]',
        'rounded-[12px]',
        'text-[11px]',
        'font-medium',
        'whitespace-nowrap',
        'table-tag',
      );

      container.appendChild(tag);
    });

    return container;
  }

  // Auto-detect data type based on value content
  function detectDataType(value) {
    if (value === null || value === undefined || value === '')
      return '';

    if (Array.isArray(value))
      return 'array';

    if (typeof value === 'number')
      return Number.isInteger(value) ? 'integer' : 'float';

    if (typeof value === 'string') {
      const num_value = Number(value);
      if (!Number.isNaN(num_value)) {
        return Number.isInteger(num_value) ? 'integer' : 'float';
      }

      const date_formats = [
        // ISO 8601 & SQL
        'YYYY-MM-DD',
        'YYYY-MM-DDTHH:mm',
        'YYYY-MM-DDTHH:mm:ss',
        'YYYY-MM-DDTHH:mm:ssZ',
        'YYYY-MM-DD HH:mm:ss',
        'YYYY-MM-DD HH:mm',
        'YYYY-MM-DDTHH:mm:ss.SSSZ',

        // Common Excel/CSV exports
        'MM/DD/YYYY',
        'DD/MM/YYYY',
        'YYYY/MM/DD',

        'DD-MM-YYYY',
        'MM-DD-YYYY',

        'DD-MMM-YYYY',
        'DD-MMM-YY',
        'MMM DD, YYYY',
        'DD.MM.YYYY',

        // Time formats
        'HH:mm',
        'HH:mm:ss',
        'HH:mm:ss.SSS',
        'YYYY-MM-DD HH:mm:ss.SSS',

        // add more formats as needed
      ];

      const date_value = dayjs(value, date_formats, true);
      if (date_value.isValid()) {
        return (value.includes('T') || value.includes(':')) ? 'timestamp' : 'date';
      }
    }

    return 'text';
  }

  // Function to determine Handsontable type
  function determineHotType(types) {
    // Remove empty strings
    const cleanTypes = types.filter(t => t && t.trim() !== '');

    // Apply your rules
    if (cleanTypes.includes('text'))
      return 'text';
    if (cleanTypes.includes('integer') || cleanTypes.includes('float'))
      return 'numeric';
    if (cleanTypes.includes('date'))
      return 'date';

    // Default fallback
    return '';
  }

  // Format boolean values as true/false
  function formatBoolean(value) {
    if (value === null || value === undefined || value === '')
      return false;
    if (typeof value === 'boolean')
      return value;
    if (typeof value === 'string') {
      const lower = value.toLowerCase();
      if (['true', '1', 'yes', 'y'].includes(lower))
        return true;
      if (['false', '0', 'no', 'n'].includes(lower))
        return false;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    return Boolean(value ?? false);
  }

  // Format value based on detected or specified data type
  function formatValue(value, data_type) {
    switch (data_type) {
      case 'integer': return formatInteger(value);
      case 'float':
      case 'numeric': return formatFloat(value);
      case 'date': return formatDate(value);
      case 'timestamp':
      case 'datetime': return formatTimestamp(value);
      case 'timestamptz': return formatTimestamp(value, true);
      case 'boolean': return formatBoolean(value);
      default: return value;
    }
  }

  // Aggregate array of values based on aggregation type
  function aggregateValues(values, aggregation = 'sum') {
    if (!Array.isArray(values) || values.length === 0) {
      return aggregation === 'count' ? 0 : null;
    }

    // Filter out null/undefined values and convert to numbers for numeric aggregations
    const filtered_values = values.filter(val => val !== null && val !== undefined);

    if (aggregation === 'count') {
      return filtered_values.length;
    }

    // Convert to numbers for numeric aggregations
    const numeric_values = filtered_values
      .map(val => typeof val === 'string' ? Number(val.replace(/,/g, '')) : Number(val))
      .filter(num => !Number.isNaN(num));

    if (numeric_values.length === 0) {
      return null;
    }

    switch (aggregation) {
      case 'sum':
        return numeric_values.reduce((sum, val) => sum + val, 0);
      case 'avg':
      case 'average':
        return numeric_values.reduce((sum, val) => sum + val, 0) / numeric_values.length;
      case 'min':
        return Math.min(...numeric_values);
      case 'max':
        return Math.max(...numeric_values);
      default:
        return numeric_values.reduce((sum, val) => sum + val, 0);
    }
  }

  function getMemberDetails(value) {
    if (!value?.length)
      return null;
    const user_email_map = keyBy(common_store.users, 'email');
    const team_name_map = keyBy(common_store.teams, 'name');

    const getUserDetails = (user_email) => {
      const emailFormat = /^[\w.!#$%&’*+/=?^`{|}~-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*$/i;
      if (emailFormat.test(user_email)) {
        const user = user_email_map[user_email];
        if (user) {
          return {
            is_user: true,
            user,
            name: common_store.get_user_or_team_name(user.uid),
          };
        }
        return { name: user_email };
      }
      else {
        const team = team_name_map[user_email];
        if (team) {
          return {
            is_team: true,
            team,
            name: team.name,
          };
        }
        return { name: user_email };
      }
    };

    if (Array.isArray(value)) {
      return value.map(getUserDetails);
    }
    else {
      return getUserDetails(value);
    }
  }

  function formatWidgetData(data, metadata, columns_mapping = {}) {
    return data.map((row) => {
      const formatted_row = row;
      for (const col of metadata.columns) {
        const column = columns_mapping[col.name] || {};
        if (column.resource_type === 'members') {
          const formatted_member_details = getMemberDetails(row[col.name]);
          if (Array.isArray(formatted_member_details)) {
            formatted_row[col.name] = formatted_member_details.map(item => item.name);
          }
          else if (formatted_member_details) {
            formatted_row[col.name] = formatted_member_details.name;
          }
        }
        if (col.type === 'array' && Array.isArray(row[col.name])) {
          formatted_row[col.name] = row[col.name].join(', ');
        }
        if (['date', 'timestamp', 'datetime', 'timestamptz'].includes(col.type)) {
          formatted_row[col.name] = formatValue(row[col.name], col.type);
        }
      }
      return formatted_row;
    });
  }

  async function buildTableColumns(data, columns) {
    const available_columns_type_map = {};

    // Loop rows asynchronously (in case detectDataType is async)
    for (const row of data.slice(0, 1000)) {
      for (const column of columns) {
        if (!available_columns_type_map[column]) {
          available_columns_type_map[column] = new Set();
        }

        // Await async detection
        const data_type = detectDataType(row[column]);
        available_columns_type_map[column].add(data_type);
      }
    }

    const table_columns = [];

    for (const column of columns) {
      const types = [...available_columns_type_map[column]];
      const hot_type = determineHotType(types);

      const allowed_types = [];
      if (hot_type === '') {
        allowed_types.push('text', 'date', 'numeric', 'timestamp', 'dropdown');
      }
      else if (hot_type === 'text') {
        allowed_types.push('text', 'dropdown');
      }
      else if (hot_type === 'date') {
        allowed_types.push('text', 'date', 'timestamp', 'dropdown');
      }
      else if (hot_type === 'numeric') {
        allowed_types.push('text', 'numeric');
      }
      else if (hot_type === 'dropdown') {
        allowed_types.push('text', 'dropdown');
      }

      table_columns.push({
        data: column,
        title: column,
        type: hot_type || 'text',
        readOnly: false,
        allowed_types,
      });
    }

    return table_columns;
  }

  return {
    formatNumber,
    formatInteger,
    formatFloat,
    formatDate,
    formatTimestamp,
    formatBoolean,
    formatArrayAsTags,
    formatValue,
    detectDataType,
    getUserTimezone,
    aggregateValues,
    formatWidgetData,
    determineHotType,
    buildTableColumns,
    getMemberDetails,
  };
}
